import React, { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FieldConfig, RenderInput } from '../../model/FieldConfig';

interface Props {
    show: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    editingRow: any;
    productDetailFields: FieldConfig[];
    dropdowns: any;
}

const AddEditProductDetailModal: React.FC<Props> = ({
    show,
    onClose,
    onSubmit,
    editingRow,
    productDetailFields,
    dropdowns
}) => {
    const {
        register,
        handleSubmit,
        reset,
        control,
        watch,
        setValue,
        formState: { errors }
    } = useForm<any>({
        mode: 'onChange',
        defaultValues: editingRow || {}
    });

    const selectedDetailProductType = watch("materialType");

    // Reset form when modal opens/closes or editingRow changes
    useEffect(() => {
        if (show) {
            if (editingRow) {
                // Reset with editing row data
                reset(editingRow);
            } else {
                // Reset to empty values for new entry
                const emptyValues = productDetailFields.reduce((acc, field) => {
                    acc[field.name] = "";
                    return acc;
                }, {} as any);
                reset(emptyValues);
            }
        }
    }, [show, editingRow, reset, productDetailFields]);

    // Handle conditional field clearing
    useEffect(() => {
        if (selectedDetailProductType === "Gold") {
            ["shape", "color", "size"].forEach((fieldName) => {
                setValue(fieldName, "");
            });
        }
    }, [selectedDetailProductType, setValue]);

    const handleClose = () => {
        // Reset form before closing
        reset();
        onClose();
    };

    const handleFormSubmit = (data: any) => {
        // If editing, ensure we have the id
        if (editingRow) {
            data.id = editingRow.id;
        }
        onSubmit(data);
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{editingRow ? 'Edit Product Detail' : 'Add Product Detail'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-2">
                    {productDetailFields.map((f) => {
                        // Hide shape, color, size fields when materialType is Gold
                        if (
                            selectedDetailProductType === "Gold" &&
                            ["shape", "color", "size"].includes(f.name)
                        ) {
                            return null;
                        }

                        return (
                            <RenderInput
                                key={f.name}
                                placeholder={`Enter ${f.label}`}
                                field={f}
                                register={register}
                                errors={errors}
                                control={control}
                            />
                        );
                    })}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn add-btn" onClick={handleClose}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn submit-btn"
                    onClick={handleSubmit(handleFormSubmit)}
                >
                    {editingRow ? 'Update' : 'Add'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddEditProductDetailModal;